import { environment } from '@/config/environment.ts';
import { enqueueSnackbar } from 'notistack';

interface ErrorContext {
  userName: string | null;
  accountId: number | null;
}

interface ErrorPayload {
  message: string;
  stackTrace?: string;
  url?: string;
  accountId?: number;
  userName?: string;
}

// Kontext aktualizovaný z AuthContext a AccountContext
let _ctx: ErrorContext = { userName: null, accountId: null };

// Per-error deduplikace: stejná chyba (message+stack) se odešle max jednou za 60 s
const DEDUP_COOLDOWN_MS = 60_000;
const _lastReported = new Map<string, number>();

export function setErrorContext(partial: Partial<ErrorContext>): void {
  _ctx = { ..._ctx, ...partial };
}

export function reportError(error: unknown): void {
  const { message, stack } = extractErrorInfo(error);
  const key = `${message}\n${stack ?? ''}`;

  const now = Date.now();
  const last = _lastReported.get(key);
  if (last && now - last < DEDUP_COOLDOWN_MS) {
    return;
  }
  _lastReported.set(key, now);

  try { enqueueSnackbar('Nastala neočekávaná chyba', { variant: 'error' }); } catch { /* provider not mounted yet */ }

  const payload: ErrorPayload = {
    message,
    stackTrace: stack,
    url: window.location.href,
    ...(typeof _ctx.accountId === 'number' ? { accountId: _ctx.accountId } : {}),
    ...(_ctx.userName ? { userName: _ctx.userName } : {}),
  };

  // fire-and-forget — záměrně tiše selže, aby logování nespustilo další chyby
  fetch(`${environment.apiUrl}/api/error-logs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {
    // záměrně ignorujeme — logování nesmí způsobit další chybu
  });
}

function extractErrorInfo(error: unknown): { message: string; stack: string | undefined } {
  if (error instanceof Error) {
    return { message: error.message, stack: error.stack };
  }
  if (typeof error === 'string') {
    return { message: error, stack: undefined };
  }
  try {
    return { message: JSON.stringify(error), stack: undefined };
  } catch {
    return { message: String(error), stack: undefined };
  }
}
